<template>
  <Loader :value="promises.fetch" class="shop-external view" :message="$metaInfo.title">
    <div v-if="image" class="shop-external-unit">
      <div class="shop-external-unit__cover-wrapper content">
        <div class="shop-external-unit__image-wrapper">
          <img :src="image.url" :alt="image.name" />
        </div>
      </div>
    </div>
    <div v-if="residential" class="shop-external__wrapper">
      <div class="shop-external__wrapper-residential card card--default">
        <div class="card__content">
          <span>{{ shop.project.name }}</span>
          <h1>{{ residential.name }}</h1>
          <p>{{ $t('shopExternalProductInfo', { shop: `${shop.project.name} - ${residential.name}` }) }}</p>
        </div>
      </div>
      <div class="shop-external__wrapper-content card card--default">
        <div v-if="showForm" class="card__content">
          <h1>{{ $t('shopExternalProductAddTitle') }}</h1>
          <form class="shop-external__form mt-4" @submit.prevent="submit">
            <CustomFields v-model="data" :schema="schema" :validator="validator" />
            <div class="shop-external__actions mt-2">
              <ButtonComponent
                :icon="['fal', 'arrow-to-right']"
                theme="primary"
                @click.prevent="submit"
                @onSuccess="onSubmit"
                :success-label="$t('completed')"
                :promise="promises.submit"
                :label="$t('confirm')"
                type="submit"
                :disabled="!validator.passes"
              />
            </div>
          </form>
        </div>
        <p v-else>{{ $t('shopExternalExpired') }}</p>
      </div>
    </div>
    <div class="shop-external__wrapper" v-if="!shop">
      <p>{{ $t('exportShopNotFound') }}. {{ $t('contactSeller') }}</p>
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'

import CustomFields, { getValidatorConfig } from '@kvass/custom-fields'
import fetch from '@/fetch'
import ShopQuery from '@/api/graphql/queries/ShopResidentialMedia.gql'
import ProductQuery from '@/api/graphql/queries/Product.gql'
import { VueComponentUpload as upload } from '@kvass/storage'
import graphql from '@/api/graphql'
import { jwtDecode } from 'jwt-decode'
import { getFormattedNumber } from '../../utils/analytics'

export default {
  mixins: [
    Validator({
      labels: 'validatorConfig.labels',
      rules: 'validatorConfig.rules',
      data: 'data',
    }),
  ],
  props: {
    id: String,
    token: String,
    returnTo: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      promises: {
        submit: null,
        fetch: null,
      },
      data: {},
      shop: {},
      product: {},
    }
  },
  computed: {
    schema() {
      return [
        {
          key: 'supplier',
          label: `${this.$t('company')} / ${this.$tc('supplier', 1)}`,
          component: 'Input',
          options: {
            size: 1,
            validation: 'required',
            props: {
              sublabel: `${this.$t('shopExternalSupplierSublabel')}`,
            },
          },
        },
        {
          key: 'pricing.value',
          label: `${this.$t('price')} ${this.$t('excludingVat')}`,
          component: 'Currency',
          options: {
            size: 1,
            validation: 'required',
            props: {
              suffix: this.$store.state.enums.Region.currency,
              sublabel:
                this.margin && `${this.$t('priceMargin')} ${this.margin} ${this.$t('addedBySupplier').toLowerCase()}`,
            },
          },
        },
        {
          key: 'category',
          label: `${this.$tc('product', 1)}${this.$tc('category', 1).toLowerCase()}`,
          component: 'Input',
          options: {
            size: 1,
            validation: 'required',
            props: {
              sublabel: `${this.$t('externalAddedByContractor')}`,
              disabled: !!this.category,
            },
          },
        },
        {
          key: 'subcategory',
          label: `${this.$t('subcategory')}`,
          component: 'Input',
          options: {
            size: 1,
            validation: 'required',
            props: {
              sublabel: !!this.subcategory
                ? this.$t('externalAddedByContractor')
                : `${this.$t('shopSubcategoryExamples')}`,
              disabled: !!this.subcategory,
            },
          },
        },
        {
          key: 'name',
          label: `${this.$t('productName')}`,
          component: 'Input',
          options: {
            size: 1,
            validation: 'required',
            props: {
              sublabel: this.margin && `${this.$t('add')} ${this.$t('productName').toLowerCase()}`,
            },
          },
        },

        {
          key: 'description',
          label: `${this.$t('description')}`,
          component: 'RichText',
          options: {
            size: 2,
            validation: 'required',
            props: {
              commands: ['heading', 'bold', 'italic', 'orderedList', 'bulletList', 'link'],
            },
          },
        },
        {
          key: 'attachments',
          label: `${this.$tc('attachment', 1)}`,
          component: 'Files',
          options: {
            size: 2,
            props: {
              accept: '.pdf',
              upload: this.upload,
            },
          },
        },
        {
          key: 'images',
          label: `${this.$tc('image', 1)}`,
          component: 'Images',
          options: {
            size: 2,
            props: {
              upload: this.upload,
              description: false,
            },
          },
        },
      ]
    },
    showForm() {
      const tokenCategory = this.tokenPayload.get('category')
      const tokenSubcategory = this.tokenPayload.get('subcategory')
      const validSubcategory =
        (this.subcategory && !this.isObjectId(this.subcategory)) || tokenSubcategory === 'external'
      const validCategory = (this.category && !this.isObjectId(this.category)) || tokenCategory === 'external'

      return validCategory && validSubcategory && !this.expired
    },
    residential() {
      if (this.shop && this.shop.reference) {
        return this.shop.reference
      }
      return null
    },
    image() {
      return (this.$path('residential.media.cover') || []).find(media => media.type.startsWith('image/'))
    },
    attributes() {
      let attrs = {
        ...this.$attrs,
        disabled: true,
      }
      return attrs
    },
    shareURL() {
      const url = new URL(window.location.href)
      return `${url.origin}${url.pathname}?token=${this.token}`
    },
    validatorConfig() {
      if (!this.schema || !this.schema.length) return {}
      return getValidatorConfig(this.schema, this.data)
    },
    tokenPayload() {
      try {
        const tokenData = jwtDecode(this.token)
        return new Map(Object.entries(tokenData))
      } catch (error) {
        return new Map()
      }
    },
    productId() {
      return this.tokenPayload.get('productId')
    },
    category() {
      if (!this.shop || !this.shop.categories) return
      const category = this.shop.categories.find(
        category =>
          category.id === this.tokenPayload.get('category') || category.label === this.tokenPayload.get('category'),
      )

      if (!category && this.tokenPayload.get('category') !== 'external') return

      const label = category
        ? category.label
        : this.tokenPayload.get('category') === 'external'
        ? this.$t('shopExternalCategoryLabel')
        : this.tokenPayload.get('category')

      this.data = {
        ...this.data,
        category: label,
      }

      return label
    },
    subcategory() {
      if (!this.shop || !this.shop.categories) return

      const subcategory = this.shop.categories
        .map(category =>
          category.categories.find(subcategory => subcategory.id === this.tokenPayload.get('subcategory')),
        )
        .filter(Boolean)[0]

      if (!subcategory || this.tokenPayload.get('subcategory') === 'external') return

      this.data.subcategory = subcategory.label
      return subcategory.label
    },
    expired() {
      const expire = this.tokenPayload.get('expire')
      return this.product && expire && expire.length > 0
    },
    margin() {
      const margin = this.tokenPayload.get('margin')
      if (margin.type === 'fixed')
        return getFormattedNumber(margin.value || 0, {
          currency: this.$store.state.enums.Region.currency,
        })

      return `${margin.value}%`
    },
  },
  methods: {
    isObjectId(id) {
      return id && id.length === 24 && id.match(/^[0-9a-fA-F]{24}$/)
    },
    submit() {
      let params = new URLSearchParams({ handler: 'post', token: this.token })

      this.promises.submit = fetch(`/api/action/preset/shop-external-product?${params.toString()}`, {
        method: 'POST',
        body: JSON.stringify({
          id: this.id,
          ...Object.fromEntries(this.tokenPayload.entries()),
          ...this.data,
          category: this.category === this.$t('shopExternalCategoryLabel') ? 'external' : this.category,
        }),
      })
    },
    onSubmit() {
      if (this.returnTo) window.location.href = this.returnTo
      else window.location.reload()
    },
    fetch() {
      this.promises.fetch = Promise.all([
        graphql.request(ShopQuery, { id: this.id }).then(({ Shop }) => (this.shop = Shop)),
        graphql.request(ProductQuery, { id: this.productId }).then(({ Product }) => (this.product = Product)),
      ])
    },
    upload(rawFile, onProgress, options = {}) {
      return upload(rawFile, onProgress, {
        ...options,
        url: `/api/action/preset/shop-external-product?handler=upload&token=${this.token}`,
      })
    },
  },
  metaInfo() {
    return {
      title: this.$t('shopExternalProductAddTitle'),
    }
  },
  created() {
    this.fetch()
  },
  components: {
    CustomFields,
  },
}
</script>

<style lang="scss">
.shop-external {
  .custom-fields {
    grid-gap: 1rem 1rem;
  }

  @media screen and (max-width: 768px) {
    .custom-fields {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .field-renderer {
        width: 100%;
        .kvass-media__thumbnails {
          margin-bottom: 1rem;
          gap: 1rem;
        }
        .kvass-media-thumbnail-actions {
          right: -10px;
        }
        .kvass-media-thumbnail-actions > * {
          height: 1.8rem;
        }
      }
    }
  }

  &-unit {
    & + .shop-external__wrapper {
      transform: translateY(-10rem);
    }
    .card__content {
      padding: 2rem;
    }

    &__cover {
      background-color: css-alpha('primary', 0.07);

      &-wrapper {
        height: 45vh;
        width: 100%;
      }
    }

    &__content {
      background: linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, white 60%);
      backdrop-filter: blur(5px);
    }

    &__image-wrapper {
      pointer-events: none;
      position: relative;
      overflow: hidden;
      width: 100%;
      height: 100%;

      img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  &__share-url {
    p {
      margin-top: 0;
    }

    border: 1px solid #f9f9f9;
    padding: 2rem 1rem;

    button {
      margin-top: 1rem;
    }
  }

  &__wrapper {
    max-width: 960px;
    margin: 0 auto;
    padding: 1rem;

    &-residential {
      background: linear-gradient(180deg, hsla(0, 0%, 100%, 0.9), #fff 60%);
      backdrop-filter: blur(5px);

      .card__content {
        span {
          font-weight: 700;
          font-size: 1.2rem;
        }

        h1 {
          margin: 0;
        }
      }
    }

    &-content {
      box-shadow: none;
    }
  }
}
</style>
